<!--
@component
CardsSpecial

See markup below.
-->

<script lang="ts">
  import { appState } from '$lib/stores/index.svelte';
  import Icon from '$lib/components/common/Icons/index.svelte';
  import { fly } from 'svelte/transition';
  import { onMount } from 'svelte';
  import { breatheParams } from '$utils/breathe';
  import type { CommentaryEntry } from '$lib/@types';
  import type { SpecialIcon, SpecialType } from './types';

  let {
    comment,
    i,
    icon,
    title,
    type = undefined,
  }: {
    comment: CommentaryEntry;
    i: number;
    icon: SpecialIcon;
    title: string;
    type?: SpecialType;
  } = $props();

  // Stagger config — kept in sync with Default.svelte.
  const MAX_DELAY = 1000;
  const STEP = 50;

  // Goal cards get an extra textured background overlay sized to the card.
  let cardW = $state<number | undefined>(undefined);
  let cardH = $state<number | undefined>(undefined);

  const isGoal = $derived(type === 'goal');
  const isMobile = $derived(appState.windowWidth <= 660);
  const iconSize = $derived(isMobile ? '2.8rem' : '4rem');
  const flyDelay = $derived(Math.min(i * STEP, MAX_DELAY));
  const cardStyle = $derived(
    cardW != null && cardH != null ?
      `--cardW:${cardW}px; --cardH:${cardH}px`
    : undefined
  );

  // "Breathing" idle drift on the icon — uses the shared helper from
  // `$utils/breathe` so a column of cards drifts gently out-of-phase
  // rather than in lockstep. The matching SCSS mixin lives in
  // `src/lib/styles/_breathe.scss`.
  const breathe = $derived(breatheParams(i, 0, 7));

  // Confetti --------------------------------------------------------------
  // Trigger a celebratory confetti burst every time a goal card crosses
  // into view. Works for both live matches (card mounts → immediately
  // in view) and finished matches (user scrolls a goal card into view,
  // out, and back again).
  // We require the card to *dwell* in view (DWELL_MS) before firing, so
  // that fast scroll-throughs — including the page's auto-scroll on
  // initial navigation, where every goal card briefly crosses the
  // viewport — don't trigger a flurry of bursts.
  let cardEl = $state<HTMLDivElement | undefined>(undefined);
  let confettiInFlight = false;

  /** How long the card must stay in view before confetti fires. */
  const DWELL_MS = 1;

  const fireConfetti = async (el: HTMLElement) => {
    // Avoid stacking bursts if the card flicks across the threshold rapidly.
    if (confettiInFlight) return;
    confettiInFlight = true;
    if (typeof window === 'undefined') {
      confettiInFlight = false;
      return;
    }
    // Respect users who prefer reduced motion.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      confettiInFlight = false;
      return;
    }

    try {
      const { default: confetti } = await import('canvas-confetti');
      const rect = el.getBoundingClientRect();
      const originX = rect.left / window.innerWidth;
      const originY = (rect.top + rect.height / 2) / window.innerHeight;
      const defaults = {
        spread: 300,
        ticks: 85,
        gravity: 0.9,
        decay: 0.9,
        startVelocity: 35,
        colors: [
          '#6aa86a',
          '#DDBA60',
          '#FFFFFF',
          // '#004f9f',
          // '#d64000',
          // '#7a7a7a',
          '#e1f4cd',
        ],
        origin: { x: originX, y: Math.max(0.1, Math.min(0.9, originY)) },
      };
      confetti({ ...defaults, particleCount: 60, scalar: 1 });
      confetti({
        ...defaults,
        particleCount: 150,
        scalar: 0.75,
        startVelocity: 25,
      });
      confetti({
        ...defaults,
        particleCount: 75,
        scalar: 1,
        startVelocity: 45,
      });
    } catch {
      // Confetti is purely decorative — fail silently if the module
      // can't load (e.g. offline or blocked by a strict CSP).
    } finally {
      // Release the lock shortly after so a re-entry into view can fire again.
      window.setTimeout(() => {
        confettiInFlight = false;
      }, 500);
    }
  };

  onMount(() => {
    if (!isGoal || !cardEl) return;
    // If IntersectionObserver isn't available, fire immediately.
    if (typeof IntersectionObserver === 'undefined') {
      fireConfetti(cardEl);
      return;
    }
    // Pending "dwell" timer — set when the card enters view, cleared
    // if it leaves before DWELL_MS elapses. Only when the timer
    // completes does the burst actually fire.
    let dwellTimer: ReturnType<typeof setTimeout> | null = null;
    const clearDwell = () => {
      if (dwellTimer !== null) {
        clearTimeout(dwellTimer);
        dwellTimer = null;
      }
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && cardEl) {
            // Start (or restart) the dwell timer. Restarting is fine
            // because we'd only get here from a fresh out→in crossing.
            clearDwell();
            dwellTimer = setTimeout(() => {
              dwellTimer = null;
              if (cardEl) fireConfetti(cardEl);
            }, DWELL_MS);
          } else {
            // Left the viewport before dwelling long enough — cancel.
            clearDwell();
          }
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(cardEl);
    return () => {
      clearDwell();
      observer.disconnect();
    };
  });
</script>

<div
  class="comment-container special"
  in:fly|global={{ y: -35, duration: 500, delay: flyDelay }}
>
  <div
    class="icon breathe"
    class:goal={isGoal}
    style:--breathe-delay={breathe.delay}
    style:--breathe-dur={breathe.dur}
  >
    <Icon name={icon} width={iconSize} height={iconSize} />
  </div>

  {#if !isMobile}
    <div class="minute">{comment.minute}'</div>
  {/if}

  <div
    class="commentary special"
    class:goal={isGoal}
    class:latest={i === 0}
    bind:clientHeight={cardH}
    bind:clientWidth={cardW}
    bind:this={cardEl}
    style={cardStyle}
  >
    <div class="card">
      {#if isMobile}
        <div class="minute">{comment.time}</div>
      {/if}
      {#if title}
        <div class="card-title">{title}</div>
      {/if}
      <div class="comment">
        <p>{comment.comment}</p>
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  @use 'layout' as *;
  @use '../../../../styles/mixins' as mixins;

  @media (max-width: 660px) {
    .minute {
      top: 0.75rem;
    }
  }

  // Subtle idle drift on each Special card icon. The `.breathe` class
  // (provided globally by `$utils/breathe`) drives the standalone
  // `translate` property, so it composes with the existing
  // `transform: translateY(-50%)` centring from _layout.scss. We just
  // override the default amplitudes for a slightly bigger drift.
  .icon {
    --breathe-amp-x: 3px;
    --breathe-amp-y: 3px;
  }

  .commentary {
    background-color: var(--theme-colour-muted-grey);
    width: calc(100% + #{$padding * 2});
    position: relative;
    margin: auto;

    @media (max-width: 660px) {
      width: 100%;
    }

    &::after {
      @include mixins.loading;
      content: '';
      position: absolute;
      display: block;
      width: var(--cardW);
      height: var(--cardH);
      top: 0;
      left: 0;
      mix-blend-mode: soft-light;
    }

    &.goal {
      background-color: var(--theme-colour-win-green);

      &::after {
        mix-blend-mode: multiply;
      }

      &::before {
        content: '';
        position: absolute;
        display: block;
        width: var(--cardH);
        height: var(--cardW);
        top: 0;
        right: 0;
        transform-box: view-box;
        transform-origin: top left;
        transform: translateX(100%) rotate(90deg);
        background-image: url('$images/branding/match-card-texture.svg');
        background-repeat: no-repeat;
        mix-blend-mode: multiply;
        background-size: 150%;
        background-position: 50% 0;
      }
    }
  }

  .card {
    display: flex;
    flex-direction: column;
    z-index: 1;
    padding: ($padding * 0.5) $padding;

    @media (max-width: 660px) {
      padding-right: ($padding * 0.25);
    }

    .minute {
      position: initial;
    }

    .card-title {
      @include mixins.font-bold;
      margin-bottom: 5px;
    }
  }
</style>
